import { SwaptionDirection } from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import {
  SecurityIdentifierType,
  TradeMessageDirection,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import {
  FutureTicketDirection,
  IBondTicket,
  IFutureTicket,
  ITicketAllocation,
  SwapTicketDirection,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";
import {
  ALLOCATIONS_KEY,
  returnPrimaryAllocationNominal,
  returnWireAllocations,
} from "./allocationRules";
import { returnFutureWireDirection } from "./futureTicketHelper";
import { returnSwapWireDirection } from "./swapTicketHelper";
import { returnSwaptionWireDirection } from "./swaptionTicketHelper";

// LQC-3027 / LQC-3029 (edit spec F1 / F2) - reopening a saved Unassigned block as an
// editable leg, and the PATCH that sends only what the user changed.

// Every block instrument with a PATCH /Trade/{type}/{blockId} (LQC-3007). An option block
// is OPT for both kinds, so its security type picks Bond Option or Swaption.
const EDIT_INSTRUMENT_BY_INSTRUMENT_TYPE: Record<string, TicketInstrument> = {
  BND: TicketInstrument.Bond,
  // PATCH /Trade/Bond takes any IsBond() block, FRN included (Q36).
  FRN: TicketInstrument.Bond,
  FUT: TicketInstrument.Future,
  SWP: TicketInstrument.Swap,
  REPO: TicketInstrument.Repo,
};

const EDIT_INSTRUMENT_BY_OPTION_SECURITY_TYPE: Record<
  string,
  TicketInstrument
> = {
  BNDO: TicketInstrument.BondOption,
  SWO: TicketInstrument.Swaption,
};

const EDIT_ENDPOINT_BY_INSTRUMENT: Record<TicketInstrument, string> = {
  [TicketInstrument.Bond]: "/Trade/Bond",
  [TicketInstrument.Future]: "/Trade/Future",
  [TicketInstrument.Swap]: "/Trade/Swap",
  [TicketInstrument.BondOption]: "/Trade/BondOption",
  [TicketInstrument.Swaption]: "/Trade/Swaption",
  [TicketInstrument.Repo]: "/Trade/RepurchaseAgreement",
};

// Edit spec: "only reachable from a saved block still in Status = Unassigned".
export const EDITABLE_BLOCK_STATUS = "Unassigned";

// The block's `source` on /blocks/unassigned (LQC-3094). Manual is one saved from this app.
export enum BlockSource {
  API = "API",
  FIX = "FIX",
  SFTP = "SFTP",
  Manual = "Manual",
}

// API and SFTP blocks are not editable here; a missing source fails safe the same way.
export const EDITABLE_BLOCK_SOURCES: string[] = [
  BlockSource.Manual,
  BlockSource.FIX,
];

export const FIX_LOCKED_REASON = "Set by the client's FIX order";

// /blocks/unassigned carries no maturity, legs, strike or expiry, so those terms stay as saved.
export const TERMS_LOCKED_REASON = "Security terms are not editable here";

export interface IEditableBlockAllocation {
  id: number;
  portfolio: string;
  notionalLeg1: number | null;
}

// The /blocks/unassigned fields an edit reads.
export interface IEditableBlock {
  id: number;
  instrumentType: string;
  securityType?: string | null;
  status: string;
  direction: string;
  notionalLeg1: number | null;
  notionalLeg2?: number | null;
  isin: string | null;
  securityName: string;
  effectiveDate: string | null;
  source?: string | null;
  allocations?: IEditableBlockAllocation[];
}

// The ticket fields an edit reads or writes, whichever instrument's ticket holds them.
export type IEditTicket = Partial<Omit<IBondTicket, "direction">> &
  Partial<Omit<IFutureTicket, "direction">> & {
    direction?: string;
    templateName?: string;
  };

// What an edit draft was opened with, so Save can send only the differences.
export interface IBlockEditBaseline {
  blockId: number;
  instrument: TicketInstrument;
  ticket: IEditTicket;
  // Row 1's saved allocation; the splits carry their own on the ticket. Not keyed by portfolio:
  // a block can hold the same portfolio twice.
  primaryAllocationId: number | null;
  source: string | null;
  securityName: string;
  // Keys the instrument itself keeps as saved (TERMS_LOCKED_REASON), on top of any FIX lock.
  termsLockedKeys: string[];
  // The sides an edit may pick, when narrower than the instrument's ticket offers.
  directions?: string[];
  // A swap's Leg 2 nominal per unit of Leg 1, so each allocation's nominal2 follows its nominal.
  nominal2Ratio: number | null;
}

export function returnEditInstrument(
  block: IEditableBlock | undefined,
): TicketInstrument | null {
  if (!block || block.status !== EDITABLE_BLOCK_STATUS) return null;
  if (!EDITABLE_BLOCK_SOURCES.includes(block.source ?? "")) return null;
  if (block.instrumentType === "OPT") {
    return (
      EDIT_INSTRUMENT_BY_OPTION_SECURITY_TYPE[block.securityType ?? ""] ?? null
    );
  }
  return EDIT_INSTRUMENT_BY_INSTRUMENT_TYPE[block.instrumentType] ?? null;
}

export function returnBlockEditEndpoint(
  instrument: TicketInstrument,
  blockId: number,
): string {
  return `${EDIT_ENDPOINT_BY_INSTRUMENT[instrument]}/${blockId}`;
}

function returnSizeKey(
  instrument: TicketInstrument,
): "nominal" | "contractCount" {
  return instrument === TicketInstrument.Future ? "contractCount" : "nominal";
}

// The ticket key behind the Security column.
export function returnEditSecurityKey(instrument: TicketInstrument): string {
  if (instrument === TicketInstrument.Future) return "bloombergSymbol";
  if (
    instrument === TicketInstrument.Swap ||
    instrument === TicketInstrument.Swaption
  ) {
    return "templateName";
  }
  return "isin";
}

// "SWO Payer 07-Oct-2027 ..." - the option type is only on the name.
export function returnSwaptionOptionType(
  securityName: string,
): "Payer" | "Receiver" | null {
  const match = /\b(Payer|Receiver)\b/.exec(securityName);
  return match ? (match[1] as "Payer" | "Receiver") : null;
}

// The API returns them unordered; oldest first keeps row 1 (the remainder) the same each edit.
function returnSortedBlockAllocations(
  block: IEditableBlock,
): IEditableBlockAllocation[] {
  return [...(block.allocations ?? [])].sort((a, b) => a.id - b.id);
}

function returnBlockAllocations(block: IEditableBlock): {
  portfolioCode: string;
  additionalAllocations: ITicketAllocation[];
} {
  const [first, ...rest] = returnSortedBlockAllocations(block);
  return {
    portfolioCode: first?.portfolio ?? "",
    additionalAllocations: rest.map((allocation) => ({
      portfolioCode: allocation.portfolio,
      nominal: allocation.notionalLeg1,
      allocationId: allocation.id,
    })),
  };
}

function returnTicketDirection(
  instrument: TicketInstrument,
  block: IEditableBlock,
): string {
  const isSell = block.direction === TradeDirection.Sell;
  if (instrument === TicketInstrument.Future) {
    return isSell ? TradeDirection.SellFuture : TradeDirection.BuyFuture;
  }
  if (instrument === TicketInstrument.Swaption) {
    const optionType = returnSwaptionOptionType(block.securityName);
    return optionType ? `${isSell ? "Sell" : "Buy"} ${optionType}` : "";
  }
  // Swaps (Pay / Receive) and repos (Borrow / Lend) keep the block's own word.
  if (
    instrument === TicketInstrument.Swap ||
    instrument === TicketInstrument.Repo
  ) {
    return block.direction;
  }
  return isSell ? TradeDirection.Sell : TradeDirection.Buy;
}

// The ticket fields a saved block fills. Trade date stays the ticket's own; it is not
// on the block and an edit never changes it.
export function returnTicketFromBlock(
  instrument: TicketInstrument,
  block: IEditableBlock,
): IEditTicket {
  const ticket: IEditTicket = {
    settlementDate: block.effectiveDate ?? "",
    ...returnBlockAllocations(block),
    direction: returnTicketDirection(instrument, block),
  };
  if (instrument === TicketInstrument.Future) {
    // A future's SecurityName is its Bloomberg ticker (README, Phase 2).
    return {
      ...ticket,
      bloombergSymbol: block.securityName,
      contractCount: block.notionalLeg1,
    };
  }
  ticket.nominal = block.notionalLeg1;
  if (
    instrument === TicketInstrument.Swap ||
    instrument === TicketInstrument.Swaption
  ) {
    return ticket;
  }
  return {
    ...ticket,
    isin: block.isin ?? "",
    securityName: block.securityName,
  };
}

function returnTermsLockedKeys(
  instrument: TicketInstrument,
  block: IEditableBlock,
): string[] {
  // /Securities/bonds has no FRNs, so the picker could only swap it for a fixed-rate bond (Q40).
  if (block.instrumentType === "FRN")
    return [returnEditSecurityKey(instrument)];
  if (
    instrument === TicketInstrument.Bond ||
    instrument === TicketInstrument.Future
  )
    return [];
  const locked = [returnEditSecurityKey(instrument)];
  // A swaption's side carries its option type; without it the side can't be shown.
  if (
    instrument === TicketInstrument.Swaption &&
    !returnSwaptionOptionType(block.securityName)
  ) {
    locked.push("direction");
  }
  return locked;
}

function returnEditDirections(
  instrument: TicketInstrument,
  block: IEditableBlock,
): string[] | undefined {
  // Buy / Sell Inflation reshape the legs, which an edit can't see.
  if (instrument === TicketInstrument.Swap)
    return [TradeDirection.Pay, TradeDirection.Receive];
  const optionType = returnSwaptionOptionType(block.securityName);
  // Payer / Receiver is the option type, a security term, so only the side flips.
  if (instrument === TicketInstrument.Swaption && optionType) {
    return [`Buy ${optionType}`, `Sell ${optionType}`];
  }
  return undefined;
}

function returnNominal2Ratio(
  instrument: TicketInstrument,
  block: IEditableBlock,
): number | null {
  if (
    instrument !== TicketInstrument.Swap ||
    !block.notionalLeg1 ||
    !block.notionalLeg2
  ) {
    return null;
  }
  return block.notionalLeg2 / block.notionalLeg1;
}

export function returnBlockEditBaseline(
  instrument: TicketInstrument,
  block: IEditableBlock,
): IBlockEditBaseline {
  return {
    blockId: block.id,
    instrument,
    ticket: returnTicketFromBlock(instrument, block),
    primaryAllocationId: returnSortedBlockAllocations(block)[0]?.id ?? null,
    source: block.source ?? null,
    securityName: block.securityName,
    termsLockedKeys: returnTermsLockedKeys(instrument, block),
    directions: returnEditDirections(instrument, block),
    nominal2Ratio: returnNominal2Ratio(instrument, block),
  };
}

function returnTicketSize(instrument: TicketInstrument, ticket: IEditTicket) {
  return instrument === TicketInstrument.Future
    ? ticket.contractCount
    : ticket.nominal;
}

function returnWireDirection(
  instrument: TicketInstrument,
  direction: string | undefined,
): TradeMessageDirection | undefined {
  if (!direction) return undefined;
  if (instrument === TicketInstrument.Future) {
    return (
      returnFutureWireDirection(direction as FutureTicketDirection) || undefined
    );
  }
  if (instrument === TicketInstrument.Swap) {
    return returnSwapWireDirection(
      direction as SwapTicketDirection,
    ) as TradeMessageDirection;
  }
  if (instrument === TicketInstrument.Swaption) {
    return returnSwaptionWireDirection(direction as SwaptionDirection)
      .direction as unknown as TradeMessageDirection;
  }
  return direction as unknown as TradeMessageDirection;
}

// The allocation set as the user edits it: the first portfolio plus the splits.
export const ALLOCATIONS_PENDING_KEY = "allocations";

export interface IAllocationRowPending {
  portfolio: boolean;
  nominal: boolean;
}

// LQC-3029 - which allocation fields differ from the block as opened, row by row.
export interface IAllocationsPending {
  primary: IAllocationRowPending;
  splits: IAllocationRowPending[];
  removedCount: number;
}

// Splits match the saved rows by allocationId, so a new row is wholly pending and a
// removed one is counted. Row 1's amount is derived, so it shows whatever moved it.
export function returnAllocationsPending(
  baseline: IBlockEditBaseline,
  ticket: IEditTicket,
): IAllocationsPending {
  const before = baseline.ticket;
  const savedSplits = before.additionalAllocations ?? [];
  const splits = ticket.additionalAllocations ?? [];
  const savedById = new Map(
    savedSplits.map((split) => [split.allocationId, split]),
  );
  const keptIds = new Set(splits.map((split) => split.allocationId));
  const returnPrimary = (t: IEditTicket) =>
    returnPrimaryAllocationNominal(
      returnTicketSize(baseline.instrument, t) ?? null,
      t.additionalAllocations ?? [],
    );
  return {
    primary: {
      portfolio: (ticket.portfolioCode ?? "") !== (before.portfolioCode ?? ""),
      nominal: returnPrimary(ticket) !== returnPrimary(before),
    },
    splits: splits.map((split) => {
      const saved =
        split.allocationId === undefined
          ? undefined
          : savedById.get(split.allocationId);
      if (!saved) return { portfolio: true, nominal: true };
      return {
        portfolio: split.portfolioCode !== saved.portfolioCode,
        nominal: split.nominal !== saved.nominal,
      };
    }),
    removedCount: savedSplits.filter(
      (split) => !keptIds.has(split.allocationId),
    ).length,
  };
}

// Row 1's amount alone is left out: a size change moves it and is pending on its own.
function hasAllocationSetChanged(pending: IAllocationsPending): boolean {
  return (
    pending.primary.portfolio ||
    pending.removedCount > 0 ||
    pending.splits.some((split) => split.portfolio || split.nominal)
  );
}

// A FIX block's side, security and IndividualAllocID belong to the client's order, so only these change.
const FIX_EDITABLE_KEYS: string[] = [
  "nominal",
  "contractCount",
  "settlementDate",
  ALLOCATIONS_PENDING_KEY,
];

// LQC-3027 - why an edit draft's field can't change, or null when it can.
export function returnEditLockReason(
  baseline: IBlockEditBaseline | undefined,
  key: string,
): string | null {
  if (!baseline) return null;
  if (baseline.source === BlockSource.FIX && !FIX_EDITABLE_KEYS.includes(key)) {
    return FIX_LOCKED_REASON;
  }
  return baseline.termsLockedKeys.includes(key) ? TERMS_LOCKED_REASON : null;
}

export function isEditKeyLocked(
  baseline: IBlockEditBaseline | undefined,
  key: string,
): boolean {
  return returnEditLockReason(baseline, key) !== null;
}

// The ticket keys an edit can send. An edit's Save only checks these: the rest of the
// ticket (maturity, legs, expiry ...) is the saved block's and never sent.
const EDIT_CHECKED_KEYS: string[] = [
  "direction",
  "nominal",
  "contractCount",
  "settlementDate",
  "isin",
  "bloombergSymbol",
  "portfolioCode",
];

export function isEditCheckedKey(
  baseline: IBlockEditBaseline,
  key: string,
): boolean {
  if (key.startsWith(ALLOCATIONS_KEY)) return true;
  return EDIT_CHECKED_KEYS.includes(key) && !isEditKeyLocked(baseline, key);
}

// LQC-3027 - the ticket keys whose value differs from the block as opened: the fields shown
// orange as pending, and the same comparison the PATCH is built from.
export function returnPendingEditKeys(
  baseline: IBlockEditBaseline,
  ticket: IEditTicket,
): Set<string> {
  const { instrument } = baseline;
  const before = baseline.ticket as Record<string, unknown>;
  const after = ticket as Record<string, unknown>;
  const sizeKey = returnSizeKey(instrument);
  const securityKey = returnEditSecurityKey(instrument);
  const keys = new Set<string>();
  if (ticket.direction !== before.direction) keys.add("direction");
  if (after[sizeKey] !== before[sizeKey]) keys.add(sizeKey);
  if (ticket.settlementDate !== before.settlementDate)
    keys.add("settlementDate");
  if ((ticket.transactionNumber ?? "") !== (before.transactionNumber ?? "")) {
    keys.add("transactionNumber");
  }
  if (after[securityKey] && after[securityKey] !== before[securityKey])
    keys.add(securityKey);
  if (hasAllocationSetChanged(returnAllocationsPending(baseline, ticket))) {
    keys.add(ALLOCATIONS_PENDING_KEY);
  }
  return keys;
}

// Only Bond and Future can swap their security; the others keep it locked.
function returnSecurityPatch(
  instrument: TicketInstrument,
  ticket: IEditTicket,
) {
  const isFuture = instrument === TicketInstrument.Future;
  return {
    identifierDetails: [
      {
        identifier: isFuture ? ticket.bloombergSymbol : ticket.isin,
        identifierType: isFuture
          ? SecurityIdentifierType.BloombergSymbol
          : SecurityIdentifierType.ISIN,
      },
    ],
  };
}

// Only the fields that differ from the baseline (spec: "only changed data"). Allocations,
// once touched, go as the complete set with each existing row's id. Null: nothing changed.
export function returnBlockEditPatch(
  baseline: IBlockEditBaseline,
  ticket: IEditTicket,
): Record<string, unknown> | null {
  const { instrument, nominal2Ratio } = baseline;
  // Locked fields never go, even if a stale draft holds a change to one.
  const pending = new Set(
    [...returnPendingEditKeys(baseline, ticket)].filter(
      (key) => !isEditKeyLocked(baseline, key),
    ),
  );
  const sizeKey = returnSizeKey(instrument);
  const size = returnTicketSize(instrument, ticket);
  const tradeDetails: Record<string, unknown> = {};

  if (pending.has("direction")) {
    tradeDetails.direction = returnWireDirection(instrument, ticket.direction);
  }
  if (pending.has(sizeKey)) tradeDetails.nominal = size;
  if (pending.has("settlementDate"))
    tradeDetails.settlementDate = ticket.settlementDate;
  if (pending.has("transactionNumber")) {
    tradeDetails.transactionNumber = ticket.transactionNumber ?? "";
  }

  const patch: Record<string, unknown> = {};
  if (Object.keys(tradeDetails).length > 0) patch.tradeDetails = tradeDetails;

  if (pending.has(returnEditSecurityKey(instrument))) {
    patch.security = returnSecurityPatch(instrument, ticket);
  }

  // A size change moves the first portfolio's amount, so the set goes with it.
  const allocationsChanged =
    pending.has(sizeKey) || pending.has(ALLOCATIONS_PENDING_KEY);
  if (allocationsChanged && typeof size === "number" && ticket.portfolioCode) {
    const splits = ticket.additionalAllocations ?? [];
    // Same order as the wire set: row 1, then the splits. 0 is a new allocation.
    const allocationIds = [
      baseline.primaryAllocationId ?? 0,
      ...splits.map((split) => split.allocationId ?? 0),
    ];
    patch.allocations = returnWireAllocations(
      ticket.portfolioCode,
      size,
      splits,
      nominal2Ratio === null ? undefined : (nominal) => nominal * nominal2Ratio,
    ).map((allocation, index) => ({
      ...allocation,
      allocationId: allocationIds[index],
    }));
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
